/**
 * SELVANE Cart Edit Size v2.3 (Global Cooldown)
 * 
 * Key improvements:
 * - Global cooldown mechanism persists across modal open/close
 * - Prevents rapid consecutive updates that trigger Cloudflare rate limiting
 * - Smart retry with exponential backoff for 429 errors
 * - Visual feedback shows success state and remaining cooldown
 */

// Global cooldown state (persists across modal sessions)
window.SelvaneCartEditSizeCooldown = window.SelvaneCartEditSizeCooldown || {
  lastUpdateTime: 0,
  COOLDOWN_MS: 8000 // 8 seconds between updates
};

class SelvaneCartEditSize {
  constructor() {
    this.modal = document.querySelector("#selvane-change-size-modal");
    if (this.modal) {
      this.scrollPosition = 0;
      this.environment = "unknown";
      this.isUpdating = false;
      this.init();
    }
  }

  init() {
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-selvane-change-size-open]");
      if (trigger) {
        e.preventDefault();
        this.openModal(trigger);
      }
    });

    const ilmTrigger = this.modal.querySelector("#selvane-ilm-trigger");
    if (ilmTrigger) {
      ilmTrigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.triggerILM();
      });
    }

    this.modal.querySelectorAll("[data-selvane-modal-close]").forEach((btn) => {
      btn.addEventListener("click", () => this.closeModal());
    });

    this.modal.querySelector("[data-selvane-modal-overlay]")?.addEventListener("click", () => this.closeModal());

    const updateBtn = this.modal.querySelector("[data-selvane-change-size-update]");
    if (updateBtn) {
      updateBtn.addEventListener("click", () => this.updateSize());
    }

    this.checkAndOpenILMOnPDP();
  }

  /**
   * Check if we're still in cooldown period
   */
  isInCooldown() {
    const cooldown = window.SelvaneCartEditSizeCooldown;
    const elapsed = Date.now() - cooldown.lastUpdateTime;
    return elapsed < cooldown.COOLDOWN_MS;
  }

  /**
   * Get remaining cooldown time in seconds
   */
  getRemainingCooldown() {
    const cooldown = window.SelvaneCartEditSizeCooldown;
    const elapsed = Date.now() - cooldown.lastUpdateTime;
    const remaining = Math.max(0, cooldown.COOLDOWN_MS - elapsed);
    return Math.ceil(remaining / 1000);
  }

  /**
   * Start cooldown timer
   */
  startCooldown() {
    window.SelvaneCartEditSizeCooldown.lastUpdateTime = Date.now();
  }

  async openModal(trigger) {
    const handle = trigger.getAttribute("data-product-handle");
    const currentSize = trigger.getAttribute("data-current-size");
    const currentColor = trigger.getAttribute("data-current-color");
    const variantId = trigger.getAttribute("data-variant-id");
    const lineItemKey = trigger.getAttribute("data-line-item-key");
    const qty = trigger.getAttribute("data-quantity");

    this.environment = trigger.getAttribute("data-environment") || "unknown";

    this.modal.querySelector("[data-selvane-change-size-title]").textContent = trigger.getAttribute("data-product-title") || "";
    this.modal.querySelector("[data-selvane-change-size-image]").src = trigger.getAttribute("data-product-image") || "";
    this.modal.querySelector("[data-selvane-change-size-current]").textContent = currentSize || "";

    const colorDisplay = this.modal.querySelector("[data-selvane-change-size-color]");
    if (colorDisplay) {
      colorDisplay.textContent = currentColor || "Default";
    }

    this.modal.setAttribute("data-current-variant-id", variantId || "");
    this.modal.setAttribute("data-line-item-key", lineItemKey || "");
    this.modal.setAttribute("data-line-item-qty", qty || "");
    this.modal.setAttribute("data-current-size", currentSize || "");
    this.modal.setAttribute("data-current-color", currentColor || "");
    this.modal.setAttribute("data-environment", this.environment);
    this.modal.setAttribute("data-product-handle", handle || "");

    const ilmTrigger = this.modal.querySelector("#selvane-ilm-trigger");
    if (ilmTrigger) {
      ilmTrigger.style.display = "inline";
    }

    this.scrollPosition = window.scrollY || window.pageYOffset;
    this.modal.setAttribute("aria-hidden", "false");

    // Reset update button state when opening modal
    this.resetUpdateButton();

    await this.loadProductData(handle, currentSize, currentColor);
  }

  closeModal() {
    this.modal.setAttribute("aria-hidden", "true");
    window.scrollTo(0, this.scrollPosition);
  }

  resetUpdateButton() {
    const updateBtn = this.modal.querySelector("[data-selvane-change-size-update]");
    if (updateBtn) {
      updateBtn.disabled = true;
      
      // Check global cooldown
      if (this.isInCooldown()) {
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Wait ${remaining}s...`;
        updateBtn.classList.add("is-cooldown");
        
        // Start countdown display
        this.startCooldownCountdown(updateBtn);
      } else {
        updateBtn.textContent = "Update size";
        updateBtn.classList.remove("is-success", "is-cooldown");
      }
    }
    this.isUpdating = false;
  }

  /**
   * Display countdown on button
   */
  startCooldownCountdown(updateBtn) {
    const interval = setInterval(() => {
      if (!this.isInCooldown()) {
        clearInterval(interval);
        updateBtn.textContent = "Update size";
        updateBtn.classList.remove("is-cooldown");
        // Re-enable if a size is selected
        const selectedSize = this.modal.querySelector(".selvane-change-size__option.is-selected");
        if (selectedSize && !this.isUpdating) {
          updateBtn.disabled = false;
        }
      } else {
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Wait ${remaining}s...`;
      }
    }, 500);
    
    // Clear interval when modal closes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "aria-hidden" && 
            this.modal.getAttribute("aria-hidden") === "true") {
          clearInterval(interval);
          observer.disconnect();
        }
      });
    });
    observer.observe(this.modal, { attributes: true });
  }

  async loadProductData(handle, currentSize, currentColor) {
    const optionsContainer = this.modal.querySelector("[data-selvane-change-size-options]");
    optionsContainer.innerHTML = '<div class="selvane-loading-dots">...</div>';

    try {
      const response = await fetch(`/products/${handle}.js`);
      const data = await response.json();
      this.renderOptions(data, currentSize, currentColor);
    } catch (error) {
      console.error("Error loading product:", error);
      optionsContainer.innerHTML = "<p>Error loading sizes.</p>";
    }
  }

  renderOptions(product, currentSize, currentColor) {
    const optionsContainer = this.modal.querySelector("[data-selvane-change-size-options]");
    optionsContainer.innerHTML = "";

    const sizeIndex = product.options.findIndex(
      (o) => o.name.toLowerCase().includes("size") || o.name.toLowerCase() === "\u5C3A\u7801"
    );
    const colorIndex = product.options.findIndex(
      (o) => o.name.toLowerCase().includes("color") || o.name.toLowerCase() === "\u989C\u8272"
    );

    if (sizeIndex === -1) {
      optionsContainer.innerHTML = "<p>No size options available.</p>";
      return;
    }

    let filteredVariants = product.variants;
    if (currentColor && colorIndex !== -1) {
      filteredVariants = product.variants.filter((v) => v.options[colorIndex] === currentColor);
      if (filteredVariants.length === 0) {
        filteredVariants = product.variants;
      }
    }

    const seenSizes = new Set();
    filteredVariants.forEach((variant) => {
      const sizeValue = variant.options[sizeIndex];
      if (seenSizes.has(sizeValue)) return;
      seenSizes.add(sizeValue);

      const btn = document.createElement("button");
      btn.className = "selvane-change-size__option";

      if (sizeValue === currentSize) {
        btn.classList.add("is-selected");
      }

      if (variant.available) {
        btn.onclick = () => this.selectSize(btn, variant.id);
      } else {
        btn.classList.add("is-disabled");
      }

      btn.textContent = sizeValue;
      optionsContainer.appendChild(btn);
    });
  }

  selectSize(btn, variantId) {
    this.modal.querySelectorAll(".selvane-change-size__option").forEach((b) => b.classList.remove("is-selected"));
    btn.classList.add("is-selected");

    const updateBtn = this.modal.querySelector("[data-selvane-change-size-update]");
    // Only enable if not in cooldown and not currently updating
    if (!this.isUpdating && !this.isInCooldown()) {
      updateBtn.disabled = false;
    }
    updateBtn.setAttribute("data-new-variant-id", String(variantId));
  }

  async updateSize() {
    const updateBtn = this.modal.querySelector("[data-selvane-change-size-update]");
    
    // Check global cooldown
    if (this.isInCooldown()) {
      const remaining = this.getRemainingCooldown();
      console.log(`SELVANE: In cooldown, ${remaining}s remaining`);
      updateBtn.textContent = `Wait ${remaining}s...`;
      return;
    }
    
    // Debounce: prevent rapid clicks
    if (this.isUpdating) {
      console.log("SELVANE: Update in progress, ignoring click");
      return;
    }

    const newVariantId = updateBtn.getAttribute("data-new-variant-id");
    const currentVariantId = this.modal.getAttribute("data-current-variant-id");
    const lineItemKey = this.modal.getAttribute("data-line-item-key");
    const env = this.modal.getAttribute("data-environment");

    this.isUpdating = true;
    updateBtn.disabled = true;
    updateBtn.textContent = "Updating...";

    try {
      await this.replaceVariantKeepQty({
        currentVariantId,
        newVariantId,
        lineItemKey,
        fallbackQty: this.modal.getAttribute("data-line-item-qty"),
      });

      // Success! Start global cooldown
      this.startCooldown();
      
      // Show success feedback
      updateBtn.textContent = "Updated ✓";
      updateBtn.classList.add("is-success");
      
      this.closeModal();

      if (env === "drawer") {
        await this.refreshDrawer();
      } else {
        await this.refreshCartPage();
      }
    } catch (error) {
      console.error("SELVANE: Update failed:", error);
      
      // Check if it's a rate limit error
      if (error.message && (error.message.includes("429") || error.message.includes("rate"))) {
        // Start a longer cooldown on rate limit
        window.SelvaneCartEditSizeCooldown.lastUpdateTime = Date.now();
        window.SelvaneCartEditSizeCooldown.COOLDOWN_MS = 10000; // 10 seconds after rate limit
        
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Rate limited. Wait ${remaining}s`;
        updateBtn.classList.add("is-cooldown");
        this.startCooldownCountdown(updateBtn);
      } else {
        alert("Failed to update size. Please try again.");
        updateBtn.disabled = false;
        updateBtn.textContent = "Update size";
      }
      this.isUpdating = false;
    }
  }

  /**
   * Fetch with smart retry for rate limiting
   */
  async fetchWithRetry(url, options, maxRetries = 3) {
    let lastError;
    const baseDelay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        // Handle rate limiting (429)
        if (response.status === 429) {
          const retryAfter = response.headers.get("Retry-After");
          let waitTime;
          
          if (retryAfter) {
            waitTime = isNaN(retryAfter) 
              ? new Date(retryAfter).getTime() - Date.now()
              : parseInt(retryAfter, 10) * 1000;
          } else {
            waitTime = baseDelay * Math.pow(2, attempt);
          }
          
          console.warn(`SELVANE: Rate limited (429). Waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
          
          if (attempt < maxRetries) {
            await this.delay(waitTime);
            continue;
          }
          
          throw new Error("Rate limit exceeded (429). Please wait and try again.");
        }

        // Check for Cloudflare challenge
        if (!response.ok) {
          const text = await response.text();
          
          if (text.includes("Verifying your connection") || text.includes("cf_chl")) {
            const waitTime = baseDelay * Math.pow(2, attempt);
            console.warn(`SELVANE: Cloudflare challenge. Waiting ${waitTime}ms before retry`);
            
            if (attempt < maxRetries) {
              await this.delay(waitTime);
              lastError = new Error("Cloudflare challenge detected");
              continue;
            }
            throw new Error("Cloudflare challenge: Please refresh the page and try again");
          }
          
          throw new Error(`Request failed: ${text.substring(0, 200)}`);
        }

        return response;
      } catch (error) {
        lastError = error;
        
        if (error.name === "TypeError" && attempt < maxRetries) {
          const waitTime = baseDelay * Math.pow(2, attempt);
          console.warn(`SELVANE: Network error. Waiting ${waitTime}ms before retry`);
          await this.delay(waitTime);
          continue;
        }
      }
    }

    throw lastError || new Error("Max retries exceeded");
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async replaceVariantKeepQty({ currentVariantId, newVariantId, lineItemKey, fallbackQty }) {
    // Step 1: Get current cart state
    const cartResponse = await fetch("/cart.js");
    const cart = await cartResponse.json();

    // Step 2: Find the item in cart
    let item = null;
    if (lineItemKey) {
      item = (cart.items || []).find((it) => it.key === lineItemKey);
    }
    if (!item) {
      const idNum = Number(currentVariantId);
      item = (cart.items || []).find((it) => Number(it.variant_id) === idNum);
    }

    if (!item) {
      throw new Error(`Current item not found in cart.js (variant_id=${currentVariantId}, key=${lineItemKey})`);
    }

    const key = item.key;
    const qty = Number(item.quantity || fallbackQty || 1);

    // Step 3: Remove the old item
    console.log("SELVANE: Removing old variant...");
    const removeResp = await this.fetchWithRetry(
      "/cart/change.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      },
      3
    );

    if (!removeResp.ok) {
      const t = await removeResp.text();
      throw new Error("Remove failed: " + t);
    }

    // Step 4: Add the new item
    console.log("SELVANE: Adding new variant...");
    const addResp = await this.fetchWithRetry(
      "/cart/add.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: qty }] }),
      },
      3
    );

    if (!addResp.ok) {
      const t = await addResp.text();
      throw new Error("Add failed: " + t);
    }

    console.log("SELVANE: Size update completed successfully");
  }

  async refreshDrawer() {
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.refresh === "function") {
        await cartDrawer.refresh();
        this.reinjectButtons();
        return;
      }

      const response = await fetch("/?sections=cart-drawer");
      if (response.ok) {
        const data = await response.json();
        if (data["cart-drawer"]) {
          const newContent = new DOMParser().parseFromString(data["cart-drawer"], "text/html").querySelector("cart-drawer");
          if (newContent && cartDrawer) {
            cartDrawer.innerHTML = newContent.innerHTML;
            this.reinjectButtons();
            return;
          }
        }
      }

      window.location.reload();
    } catch (e) {
      console.error("SELVANE: Drawer refresh failed:", e);
      window.location.reload();
    }
  }

  async refreshCartPage() {
    try {
      const text = await (await fetch(window.location.pathname)).text();
      const newCartItems = new DOMParser().parseFromString(text, "text/html").querySelector(".cart-items");
      const oldCartItems = document.querySelector(".cart-items");

      if (newCartItems && oldCartItems) {
        oldCartItems.innerHTML = newCartItems.innerHTML;
        this.reinjectButtons();
        return;
      }

      window.location.reload();
    } catch (e) {
      console.error("SELVANE: Cart page refresh failed:", e);
      window.location.reload();
    }
  }

  reinjectButtons() {
    document.querySelectorAll(".selvane-injected").forEach((el) => el.classList.remove("selvane-injected"));
    document.querySelectorAll(".selvane-adjust-size-trigger-v3").forEach((btn) => btn.remove());
  }

  triggerILM() {
    if (this.environment === "drawer") {
      this.openILMDirect();
      return;
    }

    if (this.environment === "cart") {
      const handle = this.modal.getAttribute("data-product-handle");
      if (handle) {
        window.location.href = `/products/${handle}?open_sizeguide=true`;
        return;
      }
    }

    this.openILMDirect();
  }

  openILMDirect() {
    if (window.ilm && typeof window.ilm.open === "function") {
      window.ilm.open();
      return;
    }

    const ilmModal = document.querySelector("#ilm.ilmsc-modal");
    if (ilmModal) {
      ilmModal.classList.add("ilmsc-show-modal");
      return;
    }

    let ilmButton = document.querySelector("button.ilmsc-button");
    if (ilmButton) {
      ilmButton.click();
      return;
    }

    const ilmText = document.querySelector(".ilmsc-text");
    if (ilmText) {
      ilmButton = ilmText.closest("button");
      if (ilmButton) {
        ilmButton.click();
      }
    }
  }

  checkAndOpenILMOnPDP() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("open_sizeguide") === "true") {
      const checkILM = setInterval(() => {
        if (window.ilm || document.querySelector("button.ilmsc-button")) {
          clearInterval(checkILM);
          setTimeout(() => this.openILMDirect(), 500);
        }
      }, 100);

      setTimeout(() => clearInterval(checkILM), 5000);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new SelvaneCartEditSize();
});